import React, { useContext, useState } from "react";
import PropTypes from "prop-types";
import { Field, Form, Formik } from "formik";

import TextBox from "../TextBox";
import TextArea from "../TextArea";
import Button from "../Button";
import useCommission from "../../../hooks/useCommissions";
import useQuotation from "../../../hooks/useQuotation";
import useChat from "../../../hooks/useChat";
import MessageContainer from "./MessageContainer";
import ChatSessionContext from "../../../contexts/ChatSessionContext";
import Overlay from "../../Overlay";
import LabelCheckBox from "../LabelCheckbox";
import { langComponent } from "../../../constants";
import AppContext from "../../../AppContext";

const topics = [
  {
    title: "งานออกแบบวิศวกรรมโครงสร้าง",
    name: "1",
  },
  {
    title: "งานออกแบบโครงสร้างสถาปัตย์",
    name: "2",
  },
  {
    title: "งานออกแบบตกแต่งภายใน",
    name: "3",
  },
  {
    title: "งานออกแบบภูมิทัศน์",
    name: "4",
  },
  {
    title: "งานออกแบบผลิตภัณฑ์",
    name: "5",
  },
  {
    title: "งานออกแบบวิศวกรรมเครื่องกล,ไฟฟ้าและประปา",
    name: "6",
  },
  {
    title: "งานออกแบบระบบทำความร้อน,การระบายอากาศและการปรับอากาศ",
    name: "7",
  },
];

const topicDetails = [
  {
    title: "การวางแผนผัง",
    value: 1,
  },
  {
    title: "การเขียนแบบก่อสร้าง",
    value: 2,
  },
  {
    title: "การเขียนภาพเสมือน",
    value: 3,
  },
  {
    title: "การกำหนดวัสดุและอุปกรณ์",
    value: 4,
  },
  {
    title: "การถอดปริมาณวัสดุ",
    value: 5,
  },
  {
    title: "ไฟล์งานต้นฉบับ",
    value: 6,
  },
  {
    title: "การเข้าตรวจเช็คงานที่ออกแบบ",
    value: 7,
  },
];

const CommissionForm = ({ onSuccess, onClose }) => {
  const { currentSession: session } = useContext(ChatSessionContext);
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.chat);
  const TranslationCommon = getLang(langComponent.common);
  const [step, setStep] = useState(1);
  const [toggleDisplay, setToggleDisplay] = useState(true);
  const [descriptions, setDescriptions] = useState({
    name: "",
    desc: "",
    drafts: 1,
    duration: "",
  });
  const [price, setPrice] = useState(undefined);
  const [types, setTypes] = useState(undefined);
  const { createCommission, patchCommission } = useCommission();
  const { createQuotation } = useQuotation();
  const { createChat, CHAT_TYPE } = useChat();

  const handleSubmitDescription = (
    { name, desc, drafts, duration },
    options
  ) => {
    setDescriptions({ name, desc, drafts: +drafts, duration: +duration });
    setStep(2);
  };

  const handleSubmitType = (values, options) => {
    setTypes(values);
    setStep(3);
  };

  const handleSubmitPrice = async (values, options) => {
    setPrice(values.price);

    // * Construct types
    let _types = [];
    for (const key in types) {
      if (!Object.hasOwnProperty.call(types, key)) continue;

      _types.push({
        category: +key,
        detail: types[key],
      });
    }

    // * 1. Create commission
    const commission = await createCommission({
      ...descriptions,
      price: +values.price,
      status: 0,
      commissionedBy: session.endUser._id,
      types: _types,
    });

    // * 2. Create quotation
    const quotation = await createQuotation({ commission: commission._id });

    // * 3. Patch quotation to commission
    await patchCommission(commission._id, { quotation: quotation._id });

    // * 4. Create chat
    createChat(session._id, CHAT_TYPE.QUOTATION, {
      quotationId: quotation._id,
    });

    setToggleDisplay(false);
    onSuccess();
  };

  const handleClose = () => {
    setToggleDisplay(false);
    onClose();
  };

  if (!toggleDisplay) {
    return null;
  }

  if (step === 1) {
  }

  return (
    <Overlay
      onClose={handleClose}
      content={
        <div className="chat-commission-form">
          <h3 className="chat-commission-form__header">
            - {Translation("Create Quotation")} -
          </h3>
          {step === 1 && (
            <>
              <div className="chat-commission-form__step">
                {Translation("Step 1/3 Description")}
              </div>
              <Formik
                initialValues={descriptions}
                onSubmit={handleSubmitDescription}
              >
                <Form className="chat-commission-form-content">
                  <h3 className="chat-commission-form-content__header">
                    {Translation("Create Quotation")}
                  </h3>
                  <TextBox label={Translation("Project Name")} name="name" />
                  <TextArea label={Translation("Description")} name="desc" />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <TextBox
                      label={Translation("Drafts allowed")}
                      name="drafts"
                    />
                    <TextBox
                      label={Translation("Duration")}
                      name="duration"
                      type="number"
                    />
                  </div>
                  <div className="chat-commission-form-content-cta">
                    <Button
                      label={TranslationCommon("Cancel")}
                      variant={"secondary"}
                      onClick={handleClose}
                    />
                    <Button
                      label={TranslationCommon("Continue")}
                      type="submit"
                    />
                  </div>
                </Form>
              </Formik>
            </>
          )}
          {step === 2 && (
            <>
              <div className="chat-commission-form__step">
                {Translation("Step 2/3 Work details")}
              </div>
              <Formik
                initialValues={
                  types || { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] }
                }
                onSubmit={handleSubmitType}
              >
                <Form className="chat-commission-form-content">
                  <h3 className="chat-commission-form-content__header">
                    {Translation("Define type of work")}
                  </h3>
                  <div className="chat-commission-form-content-types">
                    {topics.map((topic) => {
                      return (
                        <>
                          <label className="chat-commission-form-content-types-title">
                            {topic.title}
                          </label>
                          <div className="chat-commission-form-content-types-details">
                            {topicDetails.map((detail) => (
                              <LabelCheckBox
                                key={detail.value.toString()}
                                label={detail.title}
                                name={topic.name}
                                value={detail.value.toString()}
                              />
                            ))}
                          </div>
                        </>
                      );
                    })}
                    {/* <table>
                      <thead>
                        <tr>
                          <th rowSpan={2}>หัวข้อของการออกแบบ</th>
                          <th colSpan={8}>
                            รายละเอียดของการออกแบบที่ลูกค้าจะได้รับ
                          </th>
                        </tr>
                        <tr>
                          <th>การวางแผนผัง</th>
                          <th>การเขียนแบบก่อสร้าง</th>
                          <th>การเขียนภาพเสมือน</th>
                          <th>การกำหนดวัสดุและอุปกรณ์</th>
                          <th>การถอดปริมาณวัสดุ</th>
                          <th>ไฟล์งานต้นฉบับ</th>
                          <th>การเข้าตรวจเช็คงานที่ออกแบบ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topics.map((topic) => {
                          return (
                            <tr key={topic.name}>
                              <td>{topic.title}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table> */}
                    <div className="chat-commission-form-content-cta">
                      <Button
                        label={TranslationCommon("Return")}
                        variant={"secondary"}
                        onClick={() => setStep(1)}
                      />
                      <Button
                        label={TranslationCommon("Continue")}
                        type="submit"
                      />
                    </div>
                  </div>
                </Form>
              </Formik>
            </>
          )}
          {step === 3 && (
            <>
              <div className="chat-commission-form__step">
                {Translation("Step 3/3 Price setup")}
              </div>
              <Formik initialValues={{ price }} onSubmit={handleSubmitPrice}>
                <Form className="chat-commission-form-content">
                  <h3 className="chat-commission-form-content__header">
                    {Translation("Set up price")}
                  </h3>
                  <TextBox
                    label={Translation("Price")}
                    name="price"
                    placeholder={Translation("e.g. 100,000")}
                  />
                  <h3 className="chat-commission-form-content__header">
                    {Translation("Summary")}
                  </h3>
                  <div className="chat-commission-form-content-summary"></div>
                  <div className="chat-commission-form-content-cta">
                    <Button
                      label={TranslationCommon("Return")}
                      variant={"secondary"}
                      onClick={() => setStep(2)}
                    />
                    <Button
                      label={Translation("CreateCommission")}
                      type="submit"
                    />
                  </div>
                </Form>
              </Formik>
            </>
          )}
        </div>
      }
    />
  );
};

CommissionForm.propTypes = {
  session: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

CommissionForm.defaultProps = {
  session: {},
  onClose: () => {},
  onSuccess: () => {},
};

export default CommissionForm;
